import { Card, Grid, Typography } from "@mui/material"
import { CheckBoxControl, DebugBox, ReactHooksFormWrapper, SelectRequiredControl, TextAreaControl, TextFieldControl } from "@northpowerdev/np-react-utils"
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import useFetchJson from "../../../data/useFetchJson";
import { RadioGroupRequiredControl } from "../../styled/RadioGroupControl";
import { cutAwayText, inconclusiveNoImageryText, inconclusiveText, lineIsStillInPlaceText, oldBTSText, undergroundText } from "./LineStatusText";

const serviceLineStatusOptions = [
    {key: 'inPlace',         value: lineIsStillInPlaceText,        label: lineIsStillInPlaceText},
    {key: 'cutAway',         value: cutAwayText,                   label: cutAwayText},
    {key: 'oldBTS',          value: oldBTSText,                    label: oldBTSText},
    {key: 'underground',     value: undergroundText,               label: undergroundText},
    {key: 'noImagery',       value: inconclusiveNoImageryText,     label: inconclusiveNoImageryText},
    {key: 'inconclusive',    value: inconclusiveText,              label: inconclusiveText},
]

const visibilityOfFusesOptions = [
    {key: 'visible',    value: 'Visible',     label: 'Visible'},
    {key: 'notVisible', value: 'Not Visible', label: 'Not Visible'},
]

const locationOfFusesOptions = [
    {key: 'onNetworkPole', value: 'On Network Pole', label: 'On Network Pole'},
    {key: 'onServiceLine', value: 'On Service Line', label: 'On Service Line'},
    {key: 'inconclusive', value: 'Inconclusive', label: 'Inconclusive'},
]

const statusOfFusesOptions = [
    {key: 'fusesIn',      value: 'Fuses In',     label: 'Fuses In'},
    {key: 'fusesOut',     value: 'Fuses Out',    label: 'Fuses Out'},
    {key: 'inconclusive', value: 'Inconclusive', label: 'Inconclusive'},
]

const yesNoOptions = [
    {key: 'yes', value: 'Yes', label: 'Yes'},
    {key: 'no',  value: 'No',  label: 'No'},
]

const initialFormState = {
    decommissioningRecordExists: null,          // true | false
    decommissioningRecordServiceLineStatus: '', // 'Line Cut Away' | 'Inconclusive'
    serviceLineShownInGIS: null,                // true | false
    serviceLineInGoogleMaps: null,              // true | false
    inspectionRecordsShowFusePoint: null,       // true | false
    inspectionRecordsFuseStatus: '',            // 'Fuse In' | 'No Fuses' | 'Incoclusive'
    asBuiltShowsFuseLocation: null,             // true | false
    asBuiltFusePoleOwnership: '',               // 'Network Owned' | 'Customer Owned'

    // Assessment Audit
    gisCheckComplete: null,                     // 'Yes' | 'No'
    gisCheckComments: '',                       // 'free text'
    networkPoleOrPillar: '',                    // 'E.g. PO162'
    googleMapsCheckComplete: null,              // 'Yes' | 'No'
    googleMapsCheckComments: '',                // 'free text'
    decommissioningRecordsCheckComplete: null,  // 'Yes' | 'No'
    decommissioningRecordsCheckComments: '',    // 'free text'
    generalComments: '',                        // 'free text'
    tooHard: false,                             // true | false

    // Decision Making
    statusOfServiceLine: '',                    // 'Yes' | 'No' | 'Inconclusive'
    visibilityOfFuses: '',                      // 'Yes' | 'No' | 'Inconclusive'                    (don't ask if cut away)
    locationOfFuses: '',                        // 'Network Pole' | 'Service Line' | 'Inconclusive' (don't ask if cut away or if fuses not visible)
    statusOfFuses: '',                          // 'Fuses In' | 'Fuses Out' | 'Inconclusive'        (don't ask if cut away or if fuses not visible)

}

export default function DlspDesktopAssessmentForm({icpNumber, assessmentDetails, assignee, onClose}) {

    const { handleSubmit, control, formState: { errors }, setValue, watch, /* reset, getValues, trigger */ } = useForm({defaultValues: initialFormState});
    const watchAllFields = watch()

    const {
        isLoading: desktopAssessmentSaving,
        // data: desktopAssessmentResponse,
        load: saveDesktopAssessment,
        } = useFetchJson('/bff/decommissioned-lines-service/api/decommissioned-lines/submit-desktop-inspection', {params: {icp: icpNumber}, method: 'POST'}) 

    useEffect(() => {
        
        //
        // Triggers on start-up or assessment details change to pre-fill form fields
        // 
        if (assessmentDetails) {

            // Assessment Audit
            setValue('gisCheckComplete',                       assessmentDetails.gisCheckComplete)
            setValue('gisCheckComments',                       assessmentDetails.gisCheckComments)
            setValue('networkPoleOrPillar',                    assessmentDetails.networkPoleOrPillar)
            setValue('googleMapsCheckComplete',                assessmentDetails.googleMapsCheckComplete)
            setValue('googleMapsCheckComments',                assessmentDetails.googleMapsCheckComments)
            setValue('decommissioningRecordsCheckComplete',    assessmentDetails.decommissioningRecordsCheckComplete)
            setValue('decommissioningRecordsCheckComments',    assessmentDetails.decommissioningRecordsCheckComments)
            setValue('generalComments',                        assessmentDetails.generalComments)
            setValue('tooHard',                                assessmentDetails.tooHard ? true : false)

            // Decision Making
            setValue('statusOfServiceLine',                    assessmentDetails.statusOfServiceLine)
            setValue('visibilityOfFuses',                      assessmentDetails.visibilityOfFuses)
            setValue('locationOfFuses',                        assessmentDetails.locationOfFuses)
            setValue('statusOfFuses',                          assessmentDetails.statusOfFuses)
        }
        

    }, [assessmentDetails, setValue])

    const onSubmit = async (data) => {
        console.log('DlspDesktopAssessment onSubmit data', data)
        const desktopAssessmentOptions = {
            body: JSON.stringify(data)
        }

        const theResponse = await saveDesktopAssessment(desktopAssessmentOptions)
        if (theResponse !== undefined) {
            onClose() // tell parent component we're done
        }
    }

    const handleStatusOfServiceLineChange = (e) => {
        if ([cutAwayText, oldBTSText, undergroundText, inconclusiveNoImageryText].includes(e.target.value)) {
            setValue('visibilityOfFuses', '')
            setValue('locationOfFuses', '')
            setValue('statusOfFuses', '')
        }
    }

    const handleFusesVisibilityChange = (e) => {
        if (e.target.value === 'Not Visible') {
            setValue('locationOfFuses', '')
            setValue('statusOfFuses', '')
        }
    }

    return (
    
        <Card sx={{my:2, p:2, bgcolor: 'transparent'}}>
        <ReactHooksFormWrapper
                onSubmit={handleSubmit(onSubmit)}
                control={control}
                errors={errors}
                submitLabel="Save Desktop Assessment"
                isSubmitting={desktopAssessmentSaving}
                submittingLabel="Saving desktop assessment..."
        >

            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>

                    <Typography variant="body2">
                        Please let us know that you have completed a desktop assessment for this ICP.
                    </Typography>

                    <RadioGroupRequiredControl control={control} errors={errors} fieldName={'gisCheckComplete'}
                        fieldLabel="GIS Check is Complete" controlSx={{my: 2}} options={yesNoOptions}
                    /> <br />
                    <TextAreaControl control={control} errors={errors} fieldName={'gisCheckComments'}
                        fieldLabel="GIS Check Comments" controlSx={{width: '100%', mt: -2, mb: 2}}
                        minRows={3} maxRows={3}
                    /> <br />

                    <TextFieldControl control={control} errors={errors} fieldName={'networkPoleOrPillar'}
                        fieldLabel="Nearest Network Pole or Pillar Number" controlSx={{minWidth: '60%', mt: 0, mb: 6}}
                        textFieldHelperText="Find this in the GIS. E.g. PO162"
                    /> <br />

                    <RadioGroupRequiredControl control={control} errors={errors} fieldName={'googleMapsCheckComplete'}
                        fieldLabel="Google Maps Check is Complete" controlSx={{my: 2}} options={yesNoOptions}
                    /> <br />
                    <TextAreaControl control={control} errors={errors} fieldName={'googleMapsCheckComments'}
                        fieldLabel="Google Maps Check Comments" controlSx={{width: '100%', mt: -2, mb: 6}}
                        minRows={3} maxRows={3}
                    /> <br />

                    <RadioGroupRequiredControl control={control} errors={errors} fieldName={'decommissioningRecordsCheckComplete'}
                        fieldLabel="Decommissioning Records Check is Complete" controlSx={{my: 2}} options={yesNoOptions}
                    /> <br />
                    <TextAreaControl control={control} errors={errors} fieldName={'decommissioningRecordsCheckComments'}
                        fieldLabel="Decommissioning Records Check Comments" controlSx={{width: '100%', mt: -2, mb: 6}}
                        minRows={3} maxRows={3}
                    /> <br />

                    <Typography variant="body1" sx={{color: 'rgba(0,0,0,0.6)'}}>
                        Anything Else You Want To Say About This Task
                    </Typography>
                    <TextAreaControl control={control} errors={errors} fieldName={'generalComments'}
                        fieldLabel="General Comments" controlSx={{width: '100%', mt: 0, mb: 6}}
                        minRows={3} maxRows={3}
                    /> <br />

                    <Typography variant="body1" sx={{color: 'rgba(0,0,0,0.6)'}}>
                        Want To Flag This as a Priority for Desktop QA Review?
                    </Typography>
                    <CheckBoxControl control={control} errors={errors} fieldName={'tooHard'}
                        fieldLabel="Yes, this one belongs in the Priority QA / Too Hard basket" controlSx={{my: 2}}
                    /> <br />

                </Grid>
                <Grid item xs={12} sm={6}>

                    <Typography variant="body2">
                        To help us decide if further checks are needed, please tell us the status of the service line to this ICP.
                    </Typography>

                    <SelectRequiredControl
                        control={control}
                        errors={errors}
                        fieldName={'statusOfServiceLine'}
                        fieldLabel="What is the Status of the Service Line to this ICP?"
                        options={serviceLineStatusOptions}
                        controlSx={{minWidth: '80%'}}
                        onChange={handleStatusOfServiceLineChange}
                    /> <br />

                    {
                        watchAllFields.statusOfServiceLine && ![cutAwayText, oldBTSText, undergroundText, inconclusiveNoImageryText].includes(watchAllFields.statusOfServiceLine)
                        &&
                        <>

                            <Typography variant="body2">
                                Since the service line may still be in place, please tell us more about its connection to the network.
                            </Typography>

                            <SelectRequiredControl
                                control={control}
                                errors={errors}
                                fieldName={'visibilityOfFuses'}
                                fieldLabel="Are the fuses visible?"
                                options={visibilityOfFusesOptions}
                                controlSx={{minWidth: '50%'}}
                                onChange={handleFusesVisibilityChange}
                            /> <br />

                            {
                                watchAllFields.visibilityOfFuses && watchAllFields.visibilityOfFuses !== 'Not Visible'
                                &&
                                <>

                                    <SelectRequiredControl
                                        control={control}
                                        errors={errors}
                                        fieldName={'locationOfFuses'}
                                        fieldLabel="Where are the fuses?"
                                        options={locationOfFusesOptions}
                                        controlSx={{minWidth: '50%'}}
                                    /> <br />

                                    <SelectRequiredControl
                                        control={control}
                                        errors={errors}
                                        fieldName={'statusOfFuses'}
                                        fieldLabel="Are the fuses in or out?"
                                        options={statusOfFusesOptions}
                                        controlSx={{minWidth: '50%'}}
                                    /> <br />

                                </>
                            }
                        </>
                    }

                </Grid>
            </Grid>
                {/* <DebugBox title="assessmentDetails" stringify>{assessmentDetails}</DebugBox> */}
                <DebugBox title="errors" stringify>{errors}</DebugBox>
                <DebugBox title="watchAllFields" stringify>{watchAllFields}</DebugBox>
        </ReactHooksFormWrapper>
        </Card>
    )
}